import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AnswerButtons } from "./AnswerButtons";
import {
  asksExpectedItems,
  asksReplacementQuestion,
  diffItems,
  type FormReadCorrection,
  type ItemChange,
  isAnswered,
  NO_CORRECTED_ITEMS,
  QUESTIONS,
  REASON_LABELS,
} from "./corrections";
import { ReviewerAnswer } from "./ReviewerAnswer";
import type { CorrectionAnswer } from "./useFormReadCorrections";

interface CorrectionRowProps {
  row: FormReadCorrection;
  label: string;
  active: boolean;
  saving: boolean;
  onSelect: () => void;
  onAnswer: (answer: CorrectionAnswer) => void;
}

const MARKS: Record<ItemChange["change"], { spoken: string; visual: string; className: string }> = {
  kept: { spoken: "", visual: "", className: "" },
  removed: { spoken: "Removed: ", visual: "", className: "text-muted-foreground line-through" },
  added: { spoken: "Added: ", visual: "+ ", className: "font-medium text-emerald-700" },
};

const ItemList = ({ title, items, empty }: { title: string; items: ItemChange[]; empty: string }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{title}</p>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">{empty}</p>
    ) : (
      <ul className="space-y-0.5 text-sm">
        {items.map(({ text, change }) => (
          <li key={text} className={MARKS[change].className}>
            {MARKS[change].spoken && <span className="sr-only">{MARKS[change].spoken}</span>}
            {MARKS[change].visual && <span aria-hidden="true">{MARKS[change].visual}</span>}
            {text}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export function CorrectionRow({ row, label, active, saving, onSelect, onAnswer }: CorrectionRowProps) {
  const { extractor, corrected } = diffItems(row);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users select the row by focusing into it
    <section
      aria-label={label}
      onFocusCapture={onSelect}
      onClick={onSelect}
      className={cn("space-y-3 rounded-md border p-3", active && "border-primary ring-1 ring-primary")}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">{label}</h3>
        <Badge variant="outline">{REASON_LABELS[row.reason]}</Badge>
        {row.low_confidence && <Badge variant="destructive">Low confidence</Badge>}
        {isAnswered(row) && (
          <span className="ml-auto flex items-center gap-1 text-xs text-emerald-700">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Answered
          </span>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ItemList title="Extractor items" items={extractor} empty="None" />
        <ItemList title="Corrected items" items={corrected} empty={NO_CORRECTED_ITEMS[row.reason]} />
      </div>
      <div className="space-y-2 border-t pt-3">
        <AnswerButtons
          label={QUESTIONS.judge.label}
          hint={QUESTIONS.judge.hint}
          value={row.judge_was_right}
          onChange={(next) => onAnswer({ judge_was_right: next })}
          disabled={saving}
        />
        {asksReplacementQuestion(row) && (
          <AnswerButtons
            label={QUESTIONS.replacement.label}
            hint={QUESTIONS.replacement.hint}
            value={row.replacement_was_right}
            onChange={(next) => onAnswer({ replacement_was_right: next })}
            disabled={saving}
          />
        )}
      </div>
      {asksExpectedItems(row) && <ReviewerAnswer row={row} />}
    </section>
  );
}
